import {useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'

import {SystemColumnId} from '../../api/columns/contracts/memex-column'
import {apiGetSuggestedAssignees} from '../../api/memex-items/api-get-suggested-assignees'
import type {GetSuggestedAssigneesResponse, SuggestedAssignee} from '../../api/memex-items/contracts'
import {ItemType} from '../../api/memex-items/item-type'
import {apiSidePanelGetSuggestedAssigneesForItem} from '../../api/side-panel/api-side-panel-get-suggestions'
import {ItemKeyType} from '../../api/side-panel/contracts'
import type {MemexItemModel} from '../../models/memex-item-model'
import {ApiError} from '../../platform/api-error'
import {useSetColumnValue} from '../column-values/use-set-column-value'
import {useFindMemexItem} from '../memex-items/use-find-memex-item'
import {useSuggestionsStableContext} from './use-suggestions-stable-context'

const STALE_TIME = 5 * 60 * 1000

type FetchSuggestedAssigneesHookType = {
  /**
   * Fetches the suggested assignees from the server for a given item.
   * Updates the item's assignees column with the suggested assignees that are marked
   * as selected.
   * @returns A promise that resolves to an array of suggested assignees.
   * @param memexItemId The id of the item to fetch the suggested assignees for.
   */
  fetchSuggestedAssignees: (model: MemexItemModel) => Promise<Array<SuggestedAssignee> | Error>
  clearSuggestedAssigneesCache: (model: MemexItemModel) => void
}

const getQueryKey = (model: MemexItemModel) => ['suggested-assignees', model.getSuggestionsCacheKey()]

export const useFetchSuggestedAssignees = (): FetchSuggestedAssigneesHookType => {
  const {findMemexItem} = useFindMemexItem()
  const {setColumnValue} = useSetColumnValue()
  const {setSuggestedAssigneesForItem} = useSuggestionsStableContext()
  const queryClient = useQueryClient()

  const clearSuggestedAssigneesCache = useCallback(
    (model: MemexItemModel) => {
      return queryClient.invalidateQueries({queryKey: getQueryKey(model)})
    },
    [queryClient],
  )

  const fetchSuggestedAssignees = useCallback(
    async (model: MemexItemModel) => {
      let response
      try {
        response = await queryClient.fetchQuery({
          queryKey: getQueryKey(model),
          queryFn: async (): Promise<GetSuggestedAssigneesResponse> => {
            if (model.memexItemId?.()) {
              return apiGetSuggestedAssignees({memexProjectItemId: model.memexItemId()})
            } else {
              switch (model.contentType) {
                case ItemType.DraftIssue:
                  return apiSidePanelGetSuggestedAssigneesForItem({
                    kind: ItemKeyType.PROJECT_DRAFT_ISSUE,
                    projectItemId: model.id,
                  })
                case ItemType.Issue:
                  return apiSidePanelGetSuggestedAssigneesForItem({
                    kind: ItemKeyType.ISSUE,
                    itemId: model.itemId(),
                    repositoryId: model.ownerId(),
                  })
                default:
                  throw new Error(`Unsupported item type: ${model.contentType}`)
              }
            }
          },
          staleTime: STALE_TIME,
        })
      } catch (e) {
        const error = e instanceof Error ? e : new Error('Unknown error occurred')
        if (error instanceof ApiError) {
          if (error.code) {
            error.name = error.code
          }
        }
        setSuggestedAssigneesForItem(model.getSuggestionsCacheKey(), error)
        return error
      }

      const {suggestions} = response
      const memexItemModel = findMemexItem(model.id)

      if (memexItemModel) {
        setColumnValue(memexItemModel, {
          memexProjectColumnId: SystemColumnId.Assignees,
          value:
            suggestions
              ?.filter(suggestedAssignee => suggestedAssignee.selected)
              .sort((a, b) => a.login.localeCompare(b.login)) || null,
        })
      }

      setSuggestedAssigneesForItem(model.getSuggestionsCacheKey(), suggestions)

      return suggestions || []
    },
    [queryClient, findMemexItem, setSuggestedAssigneesForItem, setColumnValue],
  )

  return {fetchSuggestedAssignees, clearSuggestedAssigneesCache}
}
